/* eslint-disable no-useless-escape */
import Box from "@mui/material/Box";
import JoditEditor from "jodit-react";
import { useEffect, useMemo, useRef, useState } from "react";

const editorConfig = (buttons, height, disabled) => {
  return {
    readonly: disabled,
    toolbar: true,
    toolbarSticky: false,
    break: false,
    spellcheck: true,
    language: "en",
    toolbarButtonSize: "medium",
    toolbarAdaptive: false,
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    askBeforePasteHTML: true,
    askBeforePasteFromWord: true,
    //defaultActionOnPaste: "insert_clear_html",
    buttons: buttons,
    uploader: {
      insertImageAsBase64URI: true,
    },
    height: height || 500,
    minHeight: 120,
  };
};

const Editor = ({
  formik,
  name = "description",
  value,
  onChange,
  minimal,
  standard,
  height,
  isComplex,
  initVal,
  disabled,
}) => {
  const getFormikValue = () => {
    if (!isComplex) return formik.values[name];
    else
      return name.split(/[\.\[\]]+/).reduce((acc, key) => {
        if (Array.isArray(acc)) {
          return acc[parseInt(key)];
        }
        return acc[key];
      }, formik.values);
  };

  const initialContent = name ? getFormikValue() : value;

  const editor = useRef(null);
  const [content, setContent] = useState(initialContent);
  const copyStringToClipboard = function (str) {
    var el = document.createElement("textarea");
    el.value = str;
    el.setAttribute("readonly", "");
    el.style = { position: "absolute", left: "-9999px" };
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
  };

  const buttons = minimal
    ? [
        "bold",
        "strikethrough",
        "underline",
        "italic",
        "|",
        "align",
        "ul",
        "|",
        "font",
        "fontsize",
        "paragraph",
        "link",
        "|",
      ]
    : standard
      ? [
          "undo",
          "redo",
          "|",
          "bold",
          "strikethrough",
          "underline",
          "italic",
          "align",
          "|",
          "ul",
          "ol",
          "outdent",
          "indent",
          "|",
          "font",
          "fontsize",
          "brush",
          "paragraph",
          "|",
          "table",
          "link",
          "image",
          "video",
          "hr",
          "|",
        ]
      : [
          "undo",
          "redo",
          "|",
          "bold",
          "strikethrough",
          "underline",
          "italic",
          "|",
          "superscript",
          "subscript",
          "|",
          "align",
          "|",
          "ul",
          "ol",
          "outdent",
          "indent",
          "|",
          "font",
          "fontsize",
          "brush",
          "paragraph",
          "|",
          "table",
          "symbols",
          "link",
          "image",
          "video",
          "|",
          "hr",
          "eraser",
          "copyformat",
          "|",
          "selectall",
          {
            name: "copyContent",
            tooltip: "Copy HTML to Clipboard",
            iconURL: "https://img.icons8.com/ios/50/000000/copy.png",
            exec: function (editor) {
              let html = editor.value;
              copyStringToClipboard(html);
            },
          },
          "paste",
          "|",
          "find",
          "source",
          "fullsize",
          "preview",
          "print",
          "|",
        ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const config = useMemo(
    () => editorConfig(buttons, height, disabled),
    [disabled],
  );
  useEffect(() => {
    if (!content) {
      setContent(initialContent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik?.values]);

  useEffect(() => {
    initVal && setContent(initVal);
  }, [initVal]);

  const getFormikErrors = () => {
    if (!isComplex) return formik?.errors[name];
    else
      return name?.split(/[\.\[\]]+/)?.reduce((acc, key) => {
        if (Array.isArray(acc)) {
          return acc[parseInt(key)];
        }
        return acc?.[key];
      }, formik?.errors);
  };

  const getFormikTouchStatus = () => {
    if (!isComplex) return formik?.touched[name];
    else
      return name?.split(/[\.\[\]]+/)?.reduce((acc, key) => {
        if (Array.isArray(acc)) {
          return acc[parseInt(key)];
        }
        return acc?.[key];
      }, formik?.touched);
  };

  return (
    <Box
      sx={{
        "& > div": "100%",
        "& .jodit-container ": {
          border: "1px solid #ddd",
          borderRadius: "4px",
          overflow: "auto",
        },
      }}
    >
      <JoditEditor
        tabIndex={1}
        ref={editor}
        value={content}
        onBlur={(newContent) => setContent(newContent)}
        config={config}
        onChange={(v) => (name ? formik.setFieldValue(name, v) : onChange(v))}
      />
      {isComplex ? (
        <p
          style={{
            color: "#d32f2f",
            fontSize: "12px",
            marginTop: "6px",
          }}
        >
          {getFormikTouchStatus() ? <div>{getFormikErrors()}</div> : null}
        </p>
      ) : (
        <p
          style={{
            color: "#d32f2f",
            fontSize: "12px",
            marginTop: "6px",
          }}
        >
          {formik && formik.touched[name] && formik.errors[name] ? (
            <div>{formik.errors[name]}</div>
          ) : null}
        </p>
      )}
    </Box>
  );
};

export default Editor;
